// src/features/mockTests/mockTestSlice.js
// Unified API slice for all test types: full_mock, mini_mock, class_test
import { apiSlice } from "../../app/apiSlice";

// Test type constants
export const TEST_TYPES = {
  MOCK_TEST: "full_mock",
  MINI_MOCK: "mini_mock",
  CLASS_TEST: "class_test",
  BUNDLE_TEST: "bundle_test",
};

// Route mapping for each test type
export const TEST_ROUTES = {
  full_mock: "/mock-test",
  mini_mock: "/mini-mock-test",
  class_test: "/class-test",
  bundle_test: "/bundle-test",
};

// Display names for each test type
export const TEST_DISPLAY_NAMES = {
  full_mock: "Final Mock Tests",
  mini_mock: "Mini Mock Tests",
  class_test: "Class Practice Tests",
  bundle_test: "Bundle Tests",
};

// Page titles for each test type
export const TEST_PAGE_TITLES = {
  full_mock: "Final Mock Tests",
  mini_mock: "Mini Mock Tests",
  class_test: "Class Practice Tests",
  bundle_test: "Bundle Tests",
};

// Back routes for navigation
export const TEST_BACK_ROUTES = {
  full_mock: "/app/mocktests",
  mini_mock: "/app/minimocktests",
  class_test: "/app/classtests",
  bundle_test: "/app/bundles",
};

// Result routes for each test type
export const TEST_RESULT_ROUTES = {
  full_mock: "/app/mock-test-results",
  mini_mock: "/app/mini-mock-test-results",
  class_test: "/app/class-test-results",
  bundle_test: "/app/bundle-test-results",
};

// Attempt routes for each test type
export const TEST_ATTEMPT_ROUTES = {
  full_mock: "/app/mock-tests",
  mini_mock: "/app/mini-mock-tests",
  class_test: "/app/class-tests",
  bundle_test: "/app/bundle-tests",
};

export const mockTestSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ─── Fetch all student packages ──────────────────────
    getAllStudentPackages: builder.query({
      query: () => "/api/v1/en/student/package/get-all",
      transformResponse: (response) => {
        const rows = response?.data?.data?.packages ?? [];
        return rows.map((p) => ({
          _id: p.packageId._id,
          name: p.packageId.name,
          courseName: p.courseId.name,
          currentlyActive: p.currentlyActive,
        }));
      },
      providesTags: ["StudentPackages"],
      refetchOnMountOrArgChange: true,
    }),

    // ─── Fetch all mock tests for a package (unified with testType) ─────────────
    getAllMockTests: builder.query({
      query: ({ packageId, testType = "full_mock", limit, offset }) =>
        `/api/v1/en/student/mock-test/list-all/${packageId}?testType=${testType}&limit=${limit}&offset=${offset}`,
      providesTags: (result, error, { testType }) => [
        testType === "mini_mock"
          ? "MiniMockTests"
          : testType === "class_test"
          ? "ClassTests"
          : "MockTests",
      ],
      refetchOnMountOrArgChange: true,
    }),

    // ─── Fetch a single mock test by ID ─────────────────
    getMockTestById: builder.query({
      query: (testId) => `/api/v1/en/student/mock-test/${testId}`,
      transformResponse: (response) => {
        // inner `mockTests` object
        return response?.data?.data?.mockTests ?? null;
      },
      providesTags: (result, error, testId) => [
        { type: "MockTests", id: testId },
      ],
    }),

    // ─── Create test attempt ──────────────────────────────
    createTestAttempt: builder.mutation({
      query: ({
        mockTestId,
        isPracticeMode,
        oetTest,
        timerMinutes, // Legacy parameter
        oetReadingPartAMinutes,
        oetReadingPartBCMinutes,
        writingMinutes,
        speakingMinutes,
      }) => {
        console.log("Creating test attempt with payload:", {
          mockTestId,
          isPracticeMode,
          oetTest,
          oetReadingPartAMinutes,
          oetReadingPartBCMinutes,
          writingMinutes,
          speakingMinutes,
        });

        const body = {
          mockTestId,
          isPracticeMode,
          ...(oetTest ? { oetTest } : {}), // Only include oetTest if provided
        };

        // Add separate timer fields for practice mode
        if (isPracticeMode) {
          // Add the separate timers for each section if provided
          if (oetReadingPartAMinutes)
            body.oetReadingPartAMinutes = oetReadingPartAMinutes;
          if (oetReadingPartBCMinutes)
            body.oetReadingPartBCMinutes = oetReadingPartBCMinutes;
          if (writingMinutes) body.writingMinutes = writingMinutes;
          if (speakingMinutes) body.speakingMinutes = speakingMinutes;

          // Legacy support for single timer
          if (
            timerMinutes &&
            !oetReadingPartAMinutes &&
            !oetReadingPartBCMinutes &&
            !writingMinutes
          ) {
            const now = new Date();
            const testStartTime = now;
            const testEndDate = new Date(
              now.getTime() + timerMinutes * 60 * 1000
            );

            body.testStartTime = testStartTime;
            body.testEndDate = testEndDate;
          }
        }

        return {
          url: "/api/v1/en/student/test-attempt/create",
          method: "POST",
          body,
        };
      },
      transformResponse: (response) => {
        console.log("Create test attempt response:", response);
        // Extract the studentAttemptId from the response
        const studentAttemptId =
          response?.data?.data?.createStudentAttempt?._id;
        const oetTest = response?.data?.data?.createStudentAttempt?.oetTest;
        const writingId = oetTest?.writingId;

        return {
          studentAttemptId,
          writingId,
          oetTest,
          fullResponse: response.data,
        };
      },
      invalidatesTags: ["MockTests"],
    }),

    // ─── Fetch specific parts data ───────────────────────
    getMockTestData: builder.query({
      query: ({
        studentAttemptId,
        currentPart,
        currentExtract,
        isNextPart,
        listeningTestId,
        readingTestId,
        listeningPartId,
        readingPartId,
        readingQuestionId,
      }) => {
        const params = new URLSearchParams();
        // Add required testType parameter
        params.append("testType", "oet");

        if (studentAttemptId)
          params.append("studentAttemptId", studentAttemptId);
        if (currentPart) params.append("currentPart", currentPart);
        if (currentExtract !== undefined)
          params.append("currentExtract", currentExtract);
        if (isNextPart) params.append("isNextPart", "true");

        // Add support for fetching parts data for test selection
        if (listeningTestId) params.append("listeningTestId", listeningTestId);
        if (readingTestId) params.append("readingTestId", readingTestId);

        // Support for selected parts
        if (listeningPartId && listeningPartId.length > 0) {
          listeningPartId.forEach((id) => params.append("listeningPartId", id));
        }
        if (readingPartId && readingPartId.length > 0) {
          readingPartId.forEach((id) => params.append("readingPartId", id));
        }
        if (readingQuestionId && readingQuestionId.length > 0) {
          readingQuestionId.forEach((id) =>
            params.append("readingQuestionId", id)
          );
        }

        return `/api/v1/en/student/mock-test/get-mock-test-data?${params.toString()}`;
      },
      transformResponse: (response) => response.data,
      providesTags: (result, error, arg) => {
        // Provide more specific cache tags based on the request parameters
        const tags = [];
        if (arg.listeningTestId) {
          tags.push({
            type: "MockTestData",
            id: `listening-${arg.listeningTestId}`,
          });
        }
        if (arg.readingTestId) {
          tags.push({
            type: "MockTestData",
            id: `reading-${arg.readingTestId}`,
          });
        }
        if (arg.studentAttemptId) {
          tags.push({
            type: "MockTestData",
            id: `attempt-${arg.studentAttemptId}`,
          });
        }
        // Fallback tag
        if (tags.length === 0) {
          tags.push("MockTestData");
        }
        return tags;
      },
      keepUnusedDataFor: 300, // Keep data for 5 minutes instead of 0
    }),

    // ─── Submit answers for individual parts (new API) ─────────────────────────
    submitTestAnswers: builder.mutation({
      query: ({ testId, testAttemptId, studentAnswerId, answers }) => {
        const body = {
          testId,
          answers: answers.map((answer) => ({
            questionId: answer.questionId,
            givenAnswer: answer.givenAnswer,
          })),
        };
        if (
          testAttemptId &&
          typeof testAttemptId === "string" &&
          testAttemptId.trim() !== ""
        ) {
          body.testAttemptId = testAttemptId;
        }
        if (
          studentAnswerId &&
          typeof studentAnswerId === "string" &&
          studentAnswerId.trim() !== ""
        ) {
          body.studentAnswerId = studentAnswerId;
        }
        return {
          url: "/api/v1/en/student/test-attempt/submit-answer",
          method: "POST",
          body,
        };
      },
      transformResponse: (response) => {
        // Handle different possible response structures
        console.log("Raw API response:", response);

        // If response has nested data structure, extract it
        if (response?.data?.data) {
          return response.data.data;
        } else if (response?.data) {
          return response.data;
        }

        // Return response as-is if no nested structure
        return response;
      },
      invalidatesTags: ["MockTests", "SimulationTestData"],
    }),

    // ─── Submit a full mock test (legacy) ─────────────────────────
    submitMockTest: builder.mutation({
      query: ({
        id,
        listeningAnswers,
        readingAnswers,
        writingAnswer,
        writingFiles,
        speakingResponses,
        speakingFiles,
      }) => {
        const formData = new FormData();
        formData.append("listeningAnswers", JSON.stringify(listeningAnswers));
        formData.append("readingAnswers", JSON.stringify(readingAnswers));
        formData.append("writingAnswer", writingAnswer);
        formData.append("speakingResponses", JSON.stringify(speakingResponses));
        writingFiles.forEach((f) => formData.append("writingFiles", f));
        speakingFiles.forEach((f) => formData.append("speakingFiles", f));
        return {
          url: `/api/mock-tests/${id}/submit`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["MockTests"],
    }),

    // ─── Submit speaking‐only ────────────────────────────
    submitSpeakingOnly: builder.mutation({
      query: ({ id, speaking, speakingResponses, speakingFiles }) => {
        const formData = new FormData();
        formData.append("speaking", JSON.stringify(speaking));
        formData.append("speakingResponses", JSON.stringify(speakingResponses));
        speakingFiles.forEach((f) =>
          formData.append("speakingFiles", f, f.name)
        );
        return {
          url: `/api/mock-tests/submission/${id}/speaking`,
          method: "POST",
          body: formData,
        };
      },
    }),

    // ─── Upload Student Audio ─────────────────────────────
    uploadAudio: builder.mutation({
      query: (formData) => ({
        url: "/api/v1/en/student/upload-audio",
        method: "POST",
        body: formData, // Should be FormData instance!
      }),
      transformResponse: (response) => {
        // Example: { status: 200, data: { audioUrl, key, audioName, originalName } }
        return response?.data ?? {};
      },
    }),

    // ─── Upload Writing Images ─────────────────────────────
    uploadWritingImages: builder.mutation({
      query: (formData) => ({
        url: "/api/v1/en/student/upload-writing-image",
        method: "POST",
        body: formData, // Should be FormData instance with "writingImages" field
      }),
      transformResponse: (response) => {
        // Response should contain array of uploaded image data
        return response?.data ?? {};
      },
    }),

    // ─── Create Speaking Answer ───────────────────────────
    createSpeakingAnswer: builder.mutation({
      query: (payload) => ({
        url: "/api/v1/en/student/speaking-answer/create",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => response?.data ?? {},
    }),

    // ─── Create Writing Answer ───────────────────────────
    createWritingAnswer: builder.mutation({
      query: ({
        studentTestAttemptId,
        writingTestId,
        writingText,
        writingImage,
        isAiBased,
        isPictureBased,
      }) => {
        console.log("createWritingAnswer received:", {
          studentTestAttemptId,
          writingTestId,
          writingText,
          writingImage,
          isAiBased,
          isPictureBased,
        });

        const body = {
          studentAttemptId: studentTestAttemptId, // API expects studentAttemptId, not studentTestAttemptId
          writingTestId,
          isAiBased,
        };

        // Add either writingText (computer-based) or writingImage (paper-based)
        if (writingText !== undefined) {
          body.writingText = writingText;
        }
        if (writingImage !== undefined) {
          body.writingImage = writingImage;
          body.isPictureBased = true; // Add flag for paper-based submission
        }

        console.log("Final API body:", body);

        return {
          url: "/api/v1/en/student/writing-answer/create",
          method: "POST",
          body,
        };
      },
      transformResponse: (response) => response?.data ?? {},
      invalidatesTags: ["MockTests", "WritingAnswers"],
    }),

    // ─── Fetch mock‐test results ─────────────────────────
    getMockTestResults: builder.query({
      query: (testId) => `/api/mock-tests/${testId}/results`,
      providesTags: (result, error, testId) => [
        { type: "MockTestResults", id: testId },
      ],
    }),

    // ─── Get Test Attempt Results ─────────────────────────
    getTestAttemptResults: builder.query({
      query: (studentAttemptId) =>
        `/api/v1/en/student/test-attempt/show-result/${studentAttemptId}`,
      transformResponse: (response) => {
        console.log("Test attempt results response:", response);
        return response?.data ?? {};
      },
      providesTags: (result, error, studentAttemptId) => [
        { type: "TestAttemptResults", id: studentAttemptId },
      ],
    }),

    // ─── Get Practice Test Data ─────────────────────────────
    getPracticeTestData: builder.query({
      query: ({
        studentAttemptId,
        listeningId,
        readingId,
        speakingId, // <-- Add this
        speakingCardId,
        writingId,
        listeningPartIds,
        readingPartIds,
        readingQuestionIds,
        currentSection, // 'listening', 'reading', or 'writing'
      }) => {
        const params = new URLSearchParams();

        // Student attempt ID is required
        if (!studentAttemptId) {
          throw new Error(
            "studentAttemptId is required for practice test data"
          );
        }
        params.append("studentAttemptId", studentAttemptId);
        if (currentSection === "speaking") {
          if (speakingId) params.append("speakingId", speakingId);
          if (speakingCardId) params.append("speakingCardId", speakingCardId);
        }
        // For step-by-step approach: only include the section we're currently on
        if (currentSection === "listening") {
          // Only include listening parameters
          if (listeningId) params.append("listeningId", listeningId);
          // Add listening part IDs as arrays
          if (listeningPartIds && listeningPartIds.length > 0) {
            listeningPartIds.forEach((id) =>
              params.append("listeningPartId", id)
            );
          }
        } else if (currentSection === "reading") {
          // Only include reading parameters
          if (readingId) params.append("readingId", readingId);
          // Add reading part IDs and question IDs as arrays
          if (readingPartIds && readingPartIds.length > 0) {
            readingPartIds.forEach((id) => params.append("readingPartId", id));
          }
          if (readingQuestionIds && readingQuestionIds.length > 0) {
            readingQuestionIds.forEach((id) =>
              params.append("readingQuestionId", id)
            );
          }
        } else if (currentSection === "writing") {
          // Only include writing parameters
          if (writingId) params.append("writingId", writingId);
        } else {
          // If no current section specified, include all parameters (for backward compatibility)
          if (listeningId) params.append("listeningId", listeningId);
          if (readingId) params.append("readingId", readingId);
          if (writingId) params.append("writingId", writingId);

          // Add part IDs and question IDs as arrays
          if (listeningPartIds && listeningPartIds.length > 0) {
            listeningPartIds.forEach((id) =>
              params.append("listeningPartId", id)
            );
          }
          if (readingPartIds && readingPartIds.length > 0) {
            readingPartIds.forEach((id) => params.append("readingPartId", id));
          }
          if (readingQuestionIds && readingQuestionIds.length > 0) {
            readingQuestionIds.forEach((id) =>
              params.append("readingQuestionId", id)
            );
          }
        }

        console.log(
          `Fetching practice test data for section '${
            currentSection || "all"
          }':`,
          params.toString()
        );

        return {
          url: `/api/v1/en/student/mock-test/get-practice-test-data?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => {
        console.log("Practice test data response:", response);
        return response.data || {};
      },
      // Don't cache practice test data since we need fresh data for each step
      providesTags: ["PracticeTestData"],
      keepUnusedDataFor: 0, // Don't cache data
    }),
    getMockTestPdf: builder.query({
      query: (mockTestId) =>
        `/api/v1/en/student/mock-test/get-pdf/${mockTestId}`,
      // No transformResponse because you're probably getting back a PDF blob
      responseHandler: (response) => response.blob(), // For blob download
      // Optionally: tag for cache
      providesTags: (result, error, mockTestId) => [
        { type: "MockTestPdf", id: mockTestId },
      ],
    }),
    getAllTestAttemptsByMockId: builder.query({
      // Accepts: { mockTestId, limit, offset }
      query: ({ mockTestId, limit = 10, offset = 0 }) =>
        `/api/v1/en/student/test-attempt/list-all/${mockTestId}?limit=${limit}&offset=${offset}`,
      transformResponse: (response) => ({
        list: response?.data?.data?.testAttempt ?? [],
        total: response?.data?.data?.dataCount?.count ?? 0, // <-- Fixed here!
      }),
      providesTags: (result, error, params) => [
        { type: "TestAttempts", id: params.mockTestId },
      ],
    }),

    getSimulationTestData: builder.query({
      query: ({
        listeningTestId,
        readingTestId,
        writingTestId,
        speakingTestId,
        listeningOffset = 0,
        readingOffset = 0,
        speakingOffset = 0,
        studentAttemptId,
        isNextPart, // <-- add here
      }) => {
        const params = new URLSearchParams();
        // Add required testType parameter
        params.append("testType", "oetTest");

        if (listeningTestId) {
          params.append("listeningTestId", listeningTestId);
          if (listeningOffset !== undefined)
            params.append("listeningOffset", listeningOffset);
        }
        if (readingTestId) {
          params.append("readingTestId", readingTestId);
          if (readingOffset !== undefined)
            params.append("readingOffset", readingOffset);
        }
        if (writingTestId) params.append("writingTestId", writingTestId);
        if (speakingTestId) {
          params.append("speakingTestId", speakingTestId);
          // DO NOT send speakingOffset for speaking flow, only for listening/reading
          // if (speakingOffset !== undefined) params.append("speakingOffset", speakingOffset);
        }
        if (studentAttemptId) {
          params.append("studentAttemptId", studentAttemptId);
        }
        if (isNextPart) {
          // <-- NEW LINE
          params.append("isNextPart", "true");
        }

        return {
          url: `/api/v1/en/student/mock-test/get-mock-test-data?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["SimulationTestData"],
    }),
    // ─── Get All Results for a Test Attempt ─────────────
    getTestAttemptAllResults: builder.query({
      query: (testAttemptId) =>
        `/api/v1/en/student/test-attempt/show-all-result/${testAttemptId}`,
      transformResponse: (response) => response?.data?.data ?? {},
      providesTags: (result, error, testAttemptId) => [
        { type: "TestAttemptAllResults", id: testAttemptId },
      ],
    }),

    // ─── Update Test Attempt ─────────────────────────────
    updateTestAttempt: builder.mutation({
      query: ({ studentAttemptId, isCompleted }) => ({
        url: `/api/v1/en/student/test-attempt/update`,
        method: "PATCH",
        body: {
          studentAttemptId,
          isCompleted,
        },
      }),
      transformResponse: (response) => response?.data ?? {},
      invalidatesTags: (result, error, { studentAttemptId }) => [
        { type: "TestAttemptResults", id: studentAttemptId },
        { type: "TestAttemptAllResults", id: studentAttemptId },
      ],
    }),

    // ─── Get Test Attempt Graph ─────────────────────────────
    getTestAttemptGraph: builder.query({
      query: (testAttemptId) =>
        `/api/v1/en/student/test-attempt/fetch-graph/${testAttemptId}`,
      transformResponse: (response) => response?.data ?? {},
      providesTags: (result, error, testAttemptId) => [
        { type: "TestAttemptGraph", id: testAttemptId },
      ],
    }),

    // ─── Get All Comments for Writing Answer ─────────────────────────────
    getWritingComments: builder.query({
      query: ({ writingAnswerId }) => {
        return `/api/v1/en/student/writing-answer/${writingAnswerId}/comments`;
      },
      transformResponse: (response) => ({
        comments: response?.data?.data?.comments ?? [],
        total: response?.data?.data?.dataCount?.count ?? 0,
      }),
      providesTags: (result, error, { writingAnswerId }) => [
        { type: "WritingComments", id: writingAnswerId },
      ],
    }),

    // ─── Create Comment for Writing Answer ─────────────────────────────
    createWritingComment: builder.mutation({
      query: ({ writingAnswerId, commentText }) => ({
        url: `/api/v1/en/student/writing-answer/${writingAnswerId}/comments`,
        method: "POST",
        body: {
          commentText,
        },
      }),
      transformResponse: (response) => response?.data ?? {},
      invalidatesTags: (result, error, { writingAnswerId }) => [
        { type: "WritingComments", id: writingAnswerId },
      ],
    }),

    // ─── Reply to Writing Comment ─────────────────────────────
    replyToWritingComment: builder.mutation({
      query: (params) => {
        console.log(
          "API Slice - replyToWritingComment received params:",
          params
        );

        const { writingAnswerId, commentId, commentText } = params;
        const body = { commentText };
        console.log("API Slice - sending body:", body);

        return {
          url: `/api/v1/en/student/writing-answer/${writingAnswerId}/comments/${commentId}/replies`,
          method: "POST",
          body,
        };
      },
      transformResponse: (response) => response?.data ?? {},
      invalidatesTags: (result, error, { writingAnswerId, commentId }) => [
        { type: "WritingComments", id: writingAnswerId },
        { type: "CommentReplies", id: commentId },
      ],
    }),

    // ─── Delete Writing Comment ─────────────────────────────
    deleteWritingComment: builder.mutation({
      query: ({ writingAnswerId, commentId }) => ({
        url: `/api/v1/en/student/writing-answer/${writingAnswerId}/comments/${commentId}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response?.data ?? {},
      invalidatesTags: (result, error, { writingAnswerId, commentId }) => [
        { type: "WritingComments", id: writingAnswerId },
        { type: "CommentReplies", id: commentId },
      ],
    }),

    // ─── Delete Writing Comment Reply ─────────────────────────────
    deleteWritingCommentReply: builder.mutation({
      query: ({ writingAnswerId, commentId, replyId }) => ({
        url: `/api/v1/en/student/writing-answer/${writingAnswerId}/comments/${commentId}/replies/${replyId}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response?.data ?? {},
      invalidatesTags: (result, error, { writingAnswerId, commentId }) => [
        { type: "WritingComments", id: writingAnswerId },
        { type: "CommentReplies", id: commentId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllStudentPackagesQuery,
  useGetAllMockTestsQuery,
  useGetMockTestByIdQuery,
  useCreateTestAttemptMutation, // ← new create test attempt API
  useGetMockTestDataQuery,
  useLazyGetMockTestDataQuery,
  useGetPracticeTestDataQuery,
  useLazyGetPracticeTestDataQuery,
  useSubmitTestAnswersMutation, // ← new submission API
  useSubmitMockTestMutation,
  useSubmitSpeakingOnlyMutation,
  useLazyGetSimulationTestDataQuery,
  useUploadAudioMutation,
  useUploadWritingImagesMutation, // ← new writing image upload API
  useCreateSpeakingAnswerMutation,
  useCreateWritingAnswerMutation, // ← new writing submission API
  useGetMockTestResultsQuery,
  useGetTestAttemptResultsQuery, // ← new test attempt results API
  useLazyGetTestAttemptResultsQuery, // ← lazy version for manual triggering
  useGetMockTestPdfQuery,
  useGetAllTestAttemptsByMockIdQuery,
  useGetTestAttemptAllResultsQuery,
  useLazyGetAllTestAttemptsByMockIdQuery,
  useUpdateTestAttemptMutation, // ← new update test attempt API
  useGetTestAttemptGraphQuery, // ← new graph API
  useLazyGetTestAttemptGraphQuery, // ← lazy version for manual triggering
  // Comment APIs
  useGetWritingCommentsQuery, // ← new get all comments API
  useLazyGetWritingCommentsQuery, // ← lazy version for manual triggering
  useCreateWritingCommentMutation, // ← new comment creation API
  useReplyToWritingCommentMutation, // ← new comment reply API
  useDeleteWritingCommentMutation, // ← new comment deletion API
  useDeleteWritingCommentReplyMutation, // ← new comment reply deletion API
} = mockTestSlice;
